/**
 * AuthorizePage — the application-grant consent screen.
 *
 * This is Asgard's own UI, rendered on Asgard's own origin, and it is the only
 * thing standing between a third-party application and somebody's balance. Four
 * properties it has to hold, each of which is a way this is commonly broken:
 *
 * 1. **The application does not get to describe itself.** Everything shown —
 *    name, publisher, what each scope means, whether it costs money — comes from
 *    the server's answer, not from the query string. The query string is only
 *    ever forwarded for validation.
 * 2. **Nothing renders until the request validates.** An unregistered client, a
 *    redirect that is not registered, a scope it may not ask for, or plain PKCE
 *    all fail before there is anything on screen to look trustworthy.
 * 3. **It refuses to run in a frame.** A framed consent screen is a clickjacking
 *    target. nginx sends `frame-ancestors 'none'` for this path; this check is
 *    the second lock, for the case where the page is served some other way.
 * 4. **Approval is an explicit act.** There is no auto-submit, no prefill from
 *    the URL, and the button says what it does, including that the user pays.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import {
  approveAuthorizationRequest,
  describeAuthorizationRequest,
  parseAuthorizationRequest,
  type AuthorizationRequestDescription,
  type AuthorizationRequestParams,
} from '../services/grantsApi';

interface AuthorizePageProps {
  onSignInRequired: () => void;
}

type Phase = 'checking' | 'needs-sign-in' | 'ready' | 'submitting' | 'refused';

export function AuthorizePage({ onSignInRequired }: AuthorizePageProps): JSX.Element {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [phase, setPhase] = useState<Phase>('checking');
  const [request, setRequest] = useState<AuthorizationRequestParams | null>(null);
  const [description, setDescription] = useState<AuthorizationRequestDescription | null>(null);
  const [refusal, setRefusal] = useState<string>('');

  // Property 3: never render a consent screen inside somebody else's page.
  // Computed once, synchronously, so the first paint is already the refusal --
  // an effect would render the real screen for a frame first, which is exactly
  // long enough for the clickjack this check exists to prevent.
  const [framed] = useState(() => window.top !== window.self);

  // Whether the description request has been made. A REF, not state, and the
  // effect below depends on neither it nor `phase`.
  //
  // This is load-bearing. With `phase` in the dependency list, the effect
  // re-runs every time it (or anything else) sets a phase -- so clicking Cancel
  // set `refused`, which re-triggered the effect, which re-fetched the
  // description and set `ready` again: the user's refusal visibly undid itself
  // and the Allow button came back. The same loop re-enabled the buttons
  // mid-redirect after Allow, which is two clicks and two grants.
  const requested = useRef(false);

  useEffect(() => {
    if (framed || authLoading || requested.current) return;

    const parsed = parseAuthorizationRequest(window.location.search);
    if (!parsed) {
      setRefusal(t('authorize.incompleteLink'));
      setPhase('refused');
      return;
    }
    setRequest(parsed);

    if (!user) {
      // Not marked as requested: signing in must re-run this, and it will,
      // because `user` is a dependency.
      setPhase('needs-sign-in');
      return;
    }

    requested.current = true;
    describeAuthorizationRequest(parsed)
      .then((described) => {
        setDescription(described);
        setPhase('ready');
      })
      .catch((error: unknown) => {
        // Property 2: on any refusal we show a plain message and NOTHING about
        // the application, because we have not been told anything we trust.
        const message =
          (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data
            ?.error?.message ?? t('authorize.cannotAuthorize');
        setRefusal(message);
        setPhase('refused');
      });
  }, [authLoading, user, framed, t]);

  const approve = useCallback(async () => {
    if (!request) return;
    setPhase('submitting');
    try {
      const redirectTo = await approveAuthorizationRequest(request);
      // The browser performs the redirect. The code is delivered to the
      // application's own pre-registered address and never touches this page's
      // state, storage or history beyond this navigation.
      window.location.replace(redirectTo);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? t('authorize.couldNotComplete');
      setRefusal(message);
      setPhase('refused');
    }
  }, [request, t]);

  const deny = useCallback(() => {
    // Denial does not redirect: sending the user onward to an address chosen by
    // the application would let a refusal be reported as anything at all.
    //
    // Terminal by construction: `description` is cleared too, so even if
    // something later set a phase, there is nothing left to render an Allow
    // button from. A refusal that can be undone by a stray re-render is not a
    // refusal.
    setDescription(null);
    setRefusal(t('authorize.denied'));
    setPhase('refused');
  }, [t]);

  if (framed) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-white">{t('authorize.framedTitle')}</h1>
        <p className="mt-3 text-sm text-purple-200">{t('authorize.framedBody')}</p>
      </Shell>
    );
  }

  if (phase === 'checking' || authLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-3 text-purple-200">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <span>{t('authorize.checking')}</span>
        </div>
      </Shell>
    );
  }

  if (phase === 'needs-sign-in') {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-white">{t('authorize.signInTitle')}</h1>
        <p className="mt-3 text-sm text-purple-200">{t('authorize.signInBody')}</p>
        <button
          type="button"
          onClick={onSignInRequired}
          className="mt-6 min-h-[44px] w-full rounded-lg bg-purple-600 px-4 py-3 font-medium text-white hover:bg-purple-500"
        >
          {t('authorize.signIn')}
        </button>
      </Shell>
    );
  }

  if (phase === 'refused' || !description) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-white">{t('authorize.refusedTitle')}</h1>
        <p className="mt-3 text-sm text-purple-200">{refusal}</p>
      </Shell>
    );
  }

  const bills = description.scopes.some((scope) => scope.bills_the_user);

  return (
    <Shell>
      <h1 className="text-xl font-semibold text-white">
        {t('authorize.wantsAccess', { application: description.application.display_name })}
      </h1>
      <p className="mt-1 text-sm text-purple-300">
        {t('authorize.by', { publisher: description.application.publisher })}
      </p>

      <div className="mt-6 rounded-lg border border-purple-800/60 bg-black/30 p-4">
        <p className="text-sm font-medium text-purple-100">{t('authorize.willBeAbleTo')}</p>
        <ul className="mt-3 flex flex-col gap-3">
          {description.scopes.map((scope) => (
            <li key={scope.scope} className="flex flex-col">
              <span className="text-sm font-medium text-white">{scope.display_name}</span>
              <span className="text-xs text-purple-300">{scope.summary}</span>
            </li>
          ))}
        </ul>
      </div>

      {bills && (
        <p className="mt-4 rounded-lg border border-amber-700/50 bg-amber-950/30 p-3 text-sm text-amber-200">
          {t('authorize.billedWarning')}
        </p>
      )}

      <p className="mt-4 text-xs text-purple-400">{t('authorize.neverSeesPassword')}</p>

      <p className="mt-2 break-all text-xs text-purple-500">
        {t('authorize.returnedTo')}{' '}
        <span className="text-purple-300">{description.redirect_uri}</span>
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={deny}
          disabled={phase === 'submitting'}
          className="min-h-[44px] flex-1 rounded-lg border border-purple-700 px-4 py-3 font-medium text-purple-200 hover:bg-purple-900/40 disabled:opacity-50"
        >
          {t('authorize.cancel')}
        </button>
        <button
          type="button"
          onClick={approve}
          disabled={phase === 'submitting'}
          className="min-h-[44px] flex-1 rounded-lg bg-purple-600 px-4 py-3 font-medium text-white hover:bg-purple-500 disabled:opacity-50"
        >
          {phase === 'submitting'
            ? t('authorize.authorizing')
            : t('authorize.allow', { application: description.application.display_name })}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-[#0a0015] px-4 py-10 sm:px-6">
      <div className="mx-auto w-full max-w-md rounded-xl border border-purple-900/60 bg-[#12042a] p-6 sm:p-8">
        <p className="mb-6 text-xs font-semibold uppercase tracking-widest text-purple-500">
          Asgard Studio
        </p>
        {children}
      </div>
    </div>
  );
}
